import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Tent, Car, Calendar, MapPin, Plus, Trash2, Users, Check, KeyRound, X,
} from "lucide-react";
import {
  BrutalButton, BrutalCard, BrutalInput, BrutalLabel, Badge, SectionTitle,
} from "../components/common/Brutal";
import { Logo } from "../components/common/Logo";
import { api, peso } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const MAP_IMG = "https://customer-assets.emergentagent.com/job_campground-command/artifacts/ccu5bd78_IMG_9657.jpeg";

const Stepper = ({ step }) => {
  const steps = ["Type", "Zone & Dates", "Persons", "Review"];
  return (
    <div className="grid grid-cols-4 gap-2 mb-6" data-testid="booking-stepper">
      {steps.map((s, i) => (
        <div key={s} className={`px-2 py-2 border-2 border-[#3D1E6D] text-[10px] sm:text-xs font-bold uppercase tracking-wider text-center ${
          i + 1 === step ? "bg-[#E5247C] text-white shadow-[3px_3px_0_#3D1E6D]" : i + 1 < step ? "bg-[#10B981] text-white" : "bg-white text-[#3D1E6D]"
        }`}>
          <span className="display text-base mr-1">{i + 1}</span>{s}
        </div>
      ))}
    </div>
  );
};

const calcAge = (birth) => {
  if (!birth) return 0;
  const b = new Date(birth);
  const t = new Date();
  let a = t.getFullYear() - b.getFullYear();
  const m = t.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < b.getDate())) a--;
  return a < 0 ? 0 : a;
};

export default function Booking() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [zones, setZones] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [channels, setChannels] = useState([]);
  const [campingType, setCampingType] = useState("tent");
  const [zoneId, setZoneId] = useState("");
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [persons, setPersons] = useState([{
    firstname: user?.firstname || "", middlename: user?.middlename || "", lastname: user?.lastname || "", suffix: user?.suffix || "",
    birthdate: user?.birthdate || "", age: user?.birthdate ? calcAge(user.birthdate) : 0,
    email: user?.email || "", mobile_number: user?.mobile_number || "",
  }]);
  const [car, setCar] = useState({ plate_number: "", model: "", color: "" });
  const [submitting, setSubmitting] = useState(false);
  const [retrieveOpen, setRetrieveOpen] = useState(false);
  const [retrieveForm, setRetrieveForm] = useState({ booking_id: "", passcode: "" });

  useEffect(() => {
    Promise.all([api.get("/zones"), api.get("/pricing"), api.get("/payment-channels")]).then(([z, p, c]) => {
      setZones(z.data); setPricing(p.data); setChannels(c.data);
    });
  }, []);

  const maxPersons = pricing.find((p) => p.type === campingType)?.max_persons || 4;
  const filteredZones = useMemo(() => zones.filter((z) => {
    if (!z.open_for_booking) return false;
    if (campingType === "tent") return z.type === "tent" || z.type === "both";
    if (campingType === "car") return (z.type === "car" || z.type === "both") && !z.tent_only;
    return false;
  }), [zones, campingType]);

  const selectedZone = zones.find((z) => z.id === zoneId);
  const price = pricing.find((p) => p.type === campingType)?.price_per_night || 0;
  const nights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    const a = new Date(checkIn), b = new Date(checkOut);
    return Math.max(0, Math.round((b - a) / 86400000));
  }, [checkIn, checkOut]);
  const total = price * nights; // per night, not per person

  const addPerson = () => {
    if (persons.length >= maxPersons) return toast.error(`Max ${maxPersons} persons`);
    setPersons([...persons, { firstname:"", middlename:"", lastname:"", suffix:"", birthdate:"", age:0, email:"", mobile_number:"" }]);
  };
  const removePerson = (i) => setPersons(persons.length > 1 ? persons.filter((_, idx) => idx !== i) : persons);
  const updatePerson = (i, field, val) => {
    const copy = [...persons];
    copy[i] = { ...copy[i], [field]: val };
    if (field === "birthdate") copy[i].age = calcAge(val);
    setPersons(copy);
  };

  const validateStep = () => {
    if (step === 1) return true;
    if (step === 2) {
      if (!checkIn || !checkOut || nights <= 0) return toast.error("Pick valid dates") && false;
      if (!zoneId) return toast.error("Pick a zone") && false;
      return true;
    }
    if (step === 3) {
      for (const p of persons) {
        if (!p.firstname || !p.lastname || !p.birthdate || !p.email || !p.mobile_number)
          return toast.error("Fill firstname, lastname, birthdate, email, mobile for all campers") && false;
      }
      if (campingType === "car" && (!car.plate_number || !car.model || !car.color))
        return toast.error("Fill car details") && false;
      return true;
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep(Math.min(4, step + 1)); };
  const prev = () => setStep(Math.max(1, step - 1));

  const submitBooking = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        zone_id: zoneId, camping_type: campingType, check_in: checkIn, check_out: checkOut,
        persons, car_details: campingType === "car" ? car : null,
      });
      toast.success("Booking created!");
      navigate(`/payment/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed");
    } finally { setSubmitting(false); }
  };

  const submitRetrieve = async () => {
    try {
      const { data } = await api.post("/bookings/retrieve", {
        booking_id: retrieveForm.booking_id.toUpperCase(),
        passcode: retrieveForm.passcode,
      });
      // Store in localStorage so CamperHome can pick up
      const arr = JSON.parse(localStorage.getItem("kf_retrieved_bookings") || "[]");
      arr.push(data);
      localStorage.setItem("kf_retrieved_bookings", JSON.stringify(arr));
      toast.success("Booking retrieved! Showing on your dashboard.");
      setRetrieveOpen(false);
      navigate("/camper");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Retrieve failed");
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="min-h-screen bg-[#FFF4E6]">
      <header className="border-b-2 border-[#3D1E6D] bg-white">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Logo />
          <button onClick={() => navigate("/camper")} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#3D1E6D] hover:text-[#E5247C]" data-testid="booking-back-dashboard">
            <ArrowLeft className="h-3 w-3" /> Dashboard
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <SectionTitle kicker="Book Your Slot" title="Lock in your KAOGMA experience" />
        <button
          type="button"
          onClick={() => setRetrieveOpen(true)}
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-[#E5247C] hover:underline"
          data-testid="retrieve-booking-link"
        >
          <KeyRound className="h-4 w-4" /> Already booked your camping slot earlier? Click here to retrieve your booking.
        </button>

        <div className="mt-6">
          <Stepper step={step} />

          {step === 1 && (
            <BrutalCard className="p-6">
              <h3 className="display text-2xl mb-4">Choose your camping type</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { v: "tent", icon: Tent, label: "Tent Camping", desc: "Pitch your tent. Per tent, per night. Max 4 persons." },
                  { v: "car", icon: Car, label: "Car Camping", desc: "Bring your vehicle. Per tent + car, per night. Max 4 persons." },
                ].map((opt) => {
                  const p = pricing.find((x) => x.type === opt.v);
                  const active = campingType === opt.v;
                  return (
                    <button key={opt.v} type="button" onClick={() => setCampingType(opt.v)} data-testid={`camping-type-${opt.v}`}
                      className={`text-left p-5 border-2 border-[#3D1E6D] ${active ? "bg-[#FFB400] shadow-[4px_4px_0_#3D1E6D]" : "bg-white"}`}>
                      <div className="flex items-center justify-between">
                        <opt.icon className="h-7 w-7" strokeWidth={2.75} />
                        {active && <Check className="h-5 w-5 text-[#E5247C]" />}
                      </div>
                      <div className="display text-2xl mt-3">{opt.label}</div>
                      <div className="text-sm text-[#6B4E9E] mt-1">{opt.desc}</div>
                      <div className="mt-3 inline-block bg-[#E5247C] text-white px-2 py-1 text-xs font-bold uppercase">
                        {peso(p?.price_per_night || 0)} / NIGHT
                      </div>
                    </button>
                  );
                })}
              </div>
            </BrutalCard>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <BrutalCard className="overflow-hidden p-0">
                <div className="px-5 py-3 border-b-2 border-[#3D1E6D] bg-[#FFB400] flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> <span className="font-bold uppercase tracking-wider text-sm">Campsite Map</span>
                </div>
                <img src={MAP_IMG} alt="Campsite Map" className="w-full h-auto" />
              </BrutalCard>

              <BrutalCard className="p-6">
                <h3 className="display text-2xl mb-4 flex items-center gap-2"><Calendar className="h-6 w-6" /> Select dates</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <BrutalLabel>Check-in</BrutalLabel>
                    <BrutalInput type="date" min={today} value={checkIn} onChange={(e) => setCheckIn(e.target.value)} data-testid="checkin-input" />
                  </div>
                  <div>
                    <BrutalLabel>Check-out</BrutalLabel>
                    <BrutalInput type="date" min={checkIn || today} value={checkOut} onChange={(e) => setCheckOut(e.target.value)} data-testid="checkout-input" />
                  </div>
                </div>
                {nights > 0 && <div className="mt-4 text-sm text-[#6B4E9E]"><b>{nights} night{nights > 1 ? "s" : ""}</b> selected</div>}
              </BrutalCard>

              <BrutalCard className="p-6">
                <h3 className="display text-2xl mb-4">Pick your zone</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {filteredZones.map((z) => {
                    const available = z.capacity - z.occupied;
                    const active = zoneId === z.id;
                    return (
                      <button key={z.id} type="button" onClick={() => setZoneId(z.id)} disabled={available <= 0}
                        data-testid={`zone-${z.id}`}
                        className={`text-left p-4 border-2 border-[#3D1E6D] ${
                          active ? "bg-[#E5247C] text-white shadow-[4px_4px_0_#3D1E6D]" : "bg-white disabled:opacity-50"
                        }`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="display text-xl flex items-center gap-2"><MapPin className="h-4 w-4" />{z.name}</div>
                          <Badge color={active ? "yellow" : available <= 5 ? "red" : "green"}>{available} left</Badge>
                        </div>
                        <p className={`text-sm mt-2 ${active ? "text-white/90" : "text-[#6B4E9E]"}`}>{z.description}</p>
                      </button>
                    );
                  })}
                  {filteredZones.length === 0 && <div className="text-sm text-[#6B4E9E] sm:col-span-2">No zones available for this camping type.</div>}
                </div>
              </BrutalCard>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <BrutalCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="display text-2xl flex items-center gap-2"><Users className="h-6 w-6" /> Camper details</h3>
                  <BrutalButton type="button" variant="secondary" onClick={addPerson} className="px-3 py-2 text-xs" disabled={persons.length >= maxPersons} data-testid="add-person-btn">
                    <Plus className="h-3 w-3" /> Add Person ({persons.length}/{maxPersons})
                  </BrutalButton>
                </div>
                <div className="space-y-4">
                  {persons.map((p, i) => {
                    const fullName = [p.firstname, p.middlename, p.lastname, p.suffix].filter(Boolean).join(" ");
                    return (
                      <div key={i} className="kf-border bg-[#FFF4E6] p-4" data-testid={`person-row-${i}`}>
                        <div className="flex items-center justify-between mb-3">
                          <Badge color={i === 0 ? "magenta" : "yellow"}>{i === 0 ? "Primary Camper" : `Camper ${i + 1}`}</Badge>
                          {i > 0 && <button type="button" onClick={() => removePerson(i)} className="text-[#EF4444]" data-testid={`remove-person-${i}`}><Trash2 className="h-4 w-4" /></button>}
                        </div>
                        {i === 0 && fullName && (
                          <div className="mb-3 text-sm font-bold text-[#3D1E6D]" data-testid="primary-fullname">
                            Full Name: <span className="display text-xl">{fullName}</span>
                          </div>
                        )}
                        <div className="grid sm:grid-cols-4 gap-3">
                          <Field label="First Name"><BrutalInput value={p.firstname} onChange={(e) => updatePerson(i, "firstname", e.target.value)} data-testid={`person-firstname-${i}`} /></Field>
                          <Field label="Middle Name"><BrutalInput value={p.middlename} onChange={(e) => updatePerson(i, "middlename", e.target.value)} data-testid={`person-middlename-${i}`} /></Field>
                          <Field label="Last Name"><BrutalInput value={p.lastname} onChange={(e) => updatePerson(i, "lastname", e.target.value)} data-testid={`person-lastname-${i}`} /></Field>
                          <Field label="Suffix"><BrutalInput value={p.suffix} onChange={(e) => updatePerson(i, "suffix", e.target.value)} data-testid={`person-suffix-${i}`} /></Field>
                          <Field label="Birthdate"><BrutalInput type="date" value={p.birthdate} onChange={(e) => updatePerson(i, "birthdate", e.target.value)} data-testid={`person-birthdate-${i}`} /></Field>
                          <Field label="Age (auto)"><BrutalInput type="number" value={p.age || ""} readOnly data-testid={`person-age-${i}`} /></Field>
                          <Field label="Email Address"><BrutalInput type="email" value={p.email} onChange={(e) => updatePerson(i, "email", e.target.value)} data-testid={`person-email-${i}`} /></Field>
                          <Field label="Mobile Number"><BrutalInput value={p.mobile_number} onChange={(e) => updatePerson(i, "mobile_number", e.target.value)} placeholder="+63 917..." data-testid={`person-mobile-${i}`} /></Field>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </BrutalCard>

              {campingType === "car" && (
                <BrutalCard className="p-6">
                  <h3 className="display text-2xl mb-4 flex items-center gap-2"><Car className="h-6 w-6" /> Car details</h3>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <Field label="Plate Number"><BrutalInput value={car.plate_number} onChange={(e) => setCar({...car, plate_number: e.target.value.toUpperCase()})} placeholder="ABC 1234" data-testid="car-plate" /></Field>
                    <Field label="Model"><BrutalInput value={car.model} onChange={(e) => setCar({...car, model: e.target.value})} placeholder="Toyota Hilux" data-testid="car-model" /></Field>
                    <Field label="Color"><BrutalInput value={car.color} onChange={(e) => setCar({...car, color: e.target.value})} placeholder="Black" data-testid="car-color" /></Field>
                  </div>
                </BrutalCard>
              )}
            </div>
          )}

          {step === 4 && (
            <BrutalCard className="p-6" data-testid="booking-review">
              <h3 className="display text-2xl mb-4">Review your booking</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3 text-sm">
                  <Row label="Camping Type" value={campingType.toUpperCase()} />
                  <Row label="Zone" value={selectedZone?.name} />
                  <Row label="Check-in" value={checkIn} />
                  <Row label="Check-out" value={checkOut} />
                  <Row label="Nights" value={nights} />
                  <Row label="Persons" value={persons.length} />
                  {campingType === "car" && <Row label="Car" value={`${car.model} · ${car.plate_number}`} />}
                </div>
                <div className="kf-border bg-[#FFB400] p-5">
                  <div className="text-xs font-bold tracking-[0.25em] uppercase">Subtotal</div>
                  <div className="display text-5xl text-[#3D1E6D] mt-2">{peso(total)}</div>
                  <div className="mt-2 text-xs text-[#3D1E6D]/80">{peso(price)} × {nights} night{nights > 1 ? "s" : ""}</div>
                </div>
              </div>
              <div className="mt-6">
                <h4 className="text-sm font-bold uppercase tracking-widest text-[#6B4E9E] mb-2">Campers</h4>
                <div className="space-y-2">
                  {persons.map((p, i) => (
                    <div key={i} className="kf-border bg-[#FFF4E6] p-3 text-sm flex justify-between flex-wrap gap-2">
                      <span className="font-bold">{i+1}. {[p.firstname, p.middlename, p.lastname, p.suffix].filter(Boolean).join(" ")} <span className="text-[#6B4E9E]">· age {p.age}</span></span>
                      <span className="text-[#6B4E9E] text-xs">{p.email} · {p.mobile_number}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-6">
                <h4 className="text-sm font-bold uppercase tracking-widest text-[#6B4E9E] mb-2">Available payment channels</h4>
                <div className="grid sm:grid-cols-3 gap-2">
                  {channels.map((c) => (
                    <div key={c.id} className="kf-border bg-white p-3 text-xs" data-testid={`channel-preview-${c.method}`}>
                      <Badge color={c.method === "gcash" ? "magenta" : c.method === "maya" ? "purple" : "teal"}>{c.method.toUpperCase()}</Badge>
                      <div className="font-bold mt-1">{c.account_name}</div>
                      <div className="text-[#6B4E9E]">{c.mobile || c.account_number}</div>
                    </div>
                  ))}
                </div>
              </div>
            </BrutalCard>
          )}

          <div className="flex justify-between gap-3 mt-6">
            <BrutalButton variant="outline" onClick={prev} disabled={step === 1} data-testid="booking-prev"><ArrowLeft className="h-4 w-4" /> Back</BrutalButton>
            {step < 4 ? (
              <BrutalButton variant="primary" onClick={next} data-testid="booking-next">Continue <ArrowRight className="h-4 w-4" /></BrutalButton>
            ) : (
              <BrutalButton variant="primary" onClick={submitBooking} disabled={submitting} data-testid="booking-submit">
                {submitting ? "Submitting..." : "Confirm & Reserve"} <ArrowRight className="h-4 w-4" />
              </BrutalButton>
            )}
          </div>
        </div>
      </div>

      {retrieveOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={() => setRetrieveOpen(false)}>
          <div className="bg-white kf-border kf-shadow w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="retrieve-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="display text-2xl">Retrieve Booking</h3>
              <button onClick={() => setRetrieveOpen(false)}><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <BrutalLabel>Booking ID</BrutalLabel>
                <BrutalInput value={retrieveForm.booking_id} onChange={(e) => setRetrieveForm({...retrieveForm, booking_id: e.target.value.toUpperCase()})} placeholder="KG-XXXXXXXX" data-testid="retrieve-bookingid" />
              </div>
              <div>
                <BrutalLabel>10-digit Passcode</BrutalLabel>
                <BrutalInput value={retrieveForm.passcode} onChange={(e) => setRetrieveForm({...retrieveForm, passcode: e.target.value.replace(/\D/g, "").slice(0, 10)})} data-testid="retrieve-passcode" />
              </div>
              <BrutalButton variant="primary" className="w-full" onClick={submitRetrieve} data-testid="retrieve-submit">Retrieve</BrutalButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-2 border-b border-dashed border-[#3D1E6D]/30 pb-1.5">
    <span className="text-xs font-bold uppercase tracking-widest text-[#6B4E9E]">{label}</span>
    <span className="font-bold text-[#3D1E6D]">{value}</span>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <BrutalLabel>{label}</BrutalLabel>
    {children}
  </div>
);
